import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Play, Square, User, AlertCircle, AlertOctagon, Award, LogOut, X, Check, Briefcase, CheckSquare, Users, Menu, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Input } from '@/components/ui/input.js';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { useBranding } from '@/context/BrandingContext.js';
import { NotificationCenter } from './NotificationCenter.js';
import { ThemeToggle } from './ThemeToggle.js';

interface TopBarProps {
  onToggleMobile?: () => void;
}

interface ActiveTimer {
  user_id: string;
  task_id: string;
  task_title: string;
  project_name: string;
  started_at: string;
  elapsed_seconds: number;
  is_exceeded_threshold: boolean;
}

export function TopBar({ onToggleMobile }: TopBarProps = {}) {
  const navigate = useNavigate();
  const [timer, setTimer] = useState<ActiveTimer | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [criticalBlockersCount, setCriticalBlockersCount] = useState<number>(0);
  const [completedMilestonesCount, setCompletedMilestonesCount] = useState<number>(0);
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [topBarLogoError, setTopBarLogoError] = useState(false);

  useEffect(() => {
    setTopBarLogoError(false);
  }, [branding.logo_url, branding.dark_logo_url]);

  // Poll active critical blockers and completed milestone deliverable alerts
  useEffect(() => {
    let isMounted = true;
    const fetchAlerts = async () => {
      try {
        const [blockerRes, notifRes] = await Promise.allSettled([
          api.get<{ data: any[] }>('/api/v1/blockers?status=active'),
          api.get<{ data: any[] }>('/api/v1/notifications'),
        ]);
        if (!isMounted) return;
        if (blockerRes.status === 'fulfilled') {
          const list = blockerRes.value?.data || (Array.isArray(blockerRes.value) ? blockerRes.value : []);
          const count = list.filter((b: any) =>
            (b.severity === 'critical' || b.severity === 'high') && b.status !== 'resolved'
          ).length;
          setCriticalBlockersCount(count);
        }
        if (notifRes.status === 'fulfilled') {
          const notifs = notifRes.value?.data || (Array.isArray(notifRes.value) ? notifRes.value : []);
          const mCount = notifs.filter((n: any) => !n.read && n.type === 'milestone_completed').length;
          setCompletedMilestonesCount(mCount);
        }
      } catch {
        // Silently ignore polling error
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    window.addEventListener('tsos_refresh_alerts', fetchAlerts);
    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('tsos_refresh_alerts', fetchAlerts);
    };
  }, []);

  // Global Ctrl+K Search Modal state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchData, setSearchData] = useState<{
    projects: any[];
    tasks: any[];
    clients: any[];
    team: any[];
  }>({ projects: [], tasks: [], clients: [], team: [] });

  // Quick Add Modal state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickType, setQuickType] = useState<'task' | 'project' | 'client' | 'lead'>('task');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSuccess, setQuickSuccess] = useState<string | null>(null);
  const [quickProjects, setQuickProjects] = useState<any[]>([]);
  const [quickMilestones, setQuickMilestones] = useState<any[]>([]);
  const [quickForm, setQuickForm] = useState({
    title: '',
    name: '',
    description: '',
    priority: 'medium',
    email: '',
    company: '',
    amount: '',
    project_id: '',
    project_name: '',
    milestone_id: '',
    due_date: '',
  });

  // Load available projects and milestones whenever Quick Add modal opens
  useEffect(() => {
    if (!showQuickAdd) return;
    const fetchQuickData = async () => {
      try {
        const [projRes, mileRes] = await Promise.all([
          api.get<{ data: any[] }>('/api/v1/projects'),
          api.get<{ data: any[] }>('/api/v1/milestones').catch(() => ({ data: [] }))
        ]);
        const list = projRes?.data || (Array.isArray(projRes) ? projRes : []);
        setQuickProjects(list);
        const mList = (mileRes as any)?.data || (Array.isArray(mileRes) ? mileRes : []);
        setQuickMilestones(mList);
        if (list.length > 0) {
          setQuickForm((prev) => ({
            ...prev,
            project_id: prev.project_id || list[0].id,
            project_name: prev.project_name || list[0].name,
          }));
        }
      } catch (err) {
        console.error('Failed to load data for Quick Add', err);
      }
    };
    fetchQuickData();
  }, [showQuickAdd]);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearch((prev) => !prev);
      } else if (e.key === 'Escape') {
        setShowSearch(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // External trigger for Quick Add modal from floating mobile FAB or shortcuts
  useEffect(() => {
    const handleOpenQuickAdd = () => {
      setQuickType('task');
      setShowQuickAdd(true);
      setQuickSuccess(null);
    };
    window.addEventListener('tsos_open_quick_add', handleOpenQuickAdd);
    return () => window.removeEventListener('tsos_open_quick_add', handleOpenQuickAdd);
  }, []);

  // Fetch searchable data when search modal opens
  useEffect(() => {
    if (!showSearch) return;
    const role = user?.role || 'team_member';
    const isOwner = role === 'owner';
    const isManager = role === 'manager';
    const isViewer = role === 'viewer';

    const loadSearchData = async () => {
      try {
        const canViewClients = isOwner || isManager;
        const canViewTeam = !isViewer;
        const [pRes, tRes, cRes, tmRes] = await Promise.allSettled([
          api.get<{ data: any[] }>('/api/v1/projects'),
          api.get<{ data: any[] }>('/api/v1/tasks'),
          canViewClients ? api.get<{ data: any[] }>('/api/v1/clients') : Promise.resolve({ data: [] }),
          canViewTeam ? api.get<{ data: any[] }>('/api/v1/team') : Promise.resolve({ data: [] }),
        ]);
        setSearchData({
          projects: pRes.status === 'fulfilled' ? pRes.value?.data || [] : [],
          tasks: tRes.status === 'fulfilled' ? tRes.value?.data || [] : [],
          clients: cRes.status === 'fulfilled' ? cRes.value?.data || [] : [],
          team: tmRes.status === 'fulfilled' ? tmRes.value?.data || [] : [],
        });
      } catch (err) {
        console.error('Failed to load search index', err);
      }
    };
    loadSearchData();
  }, [showSearch, user?.role]);

  // Poll timer status every 30s
  useEffect(() => {
    let isMounted = true;
    const fetchTimer = async () => {
      try {
        const res = await api.get<{ data: ActiveTimer | null }>('/api/v1/time/timer');
        if (!isMounted) return;
        if (res?.data) {
          setTimer(res.data);
          // Calculate elapsed seconds from started_at
          const startedAt = new Date(res.data.started_at).getTime();
          const now = Date.now();
          const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
          setSeconds(elapsed);
        } else {
          setTimer(null);
          setSeconds(0);
        }
      } catch (e) {
        console.error('Failed to poll active timer', e);
      }
    };

    fetchTimer();
    const interval = setInterval(fetchTimer, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Visual local 1s ticker for running timer
  useEffect(() => {
    if (!timer) return;
    const tickInterval = setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(tickInterval);
  }, [timer]);

  const formatTimer = (totalSec: number) => {
    const hrs = Math.floor(totalSec / 3600).toString().padStart(2, '0');
    const mins = Math.floor((totalSec % 3600) / 60).toString().padStart(2, '0');
    const secs = (totalSec % 60).toString().padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const handleStopTimer = async () => {
    try {
      await api.post('/api/v1/time/timer/stop', {
        work_note: 'Stopped from topbar timer control',
      });
      setTimer(null);
      setSeconds(0);
    } catch (e) {
      console.error('Failed to stop timer', e);
    }
  };

  const handleQuickAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setQuickSuccess(null);

    try {
      if (quickType === 'task') {
        const pId = quickForm.project_id || (quickProjects[0]?.id || '');
        const targetProj = quickProjects.find((p) => p.id === pId);
        const pName = targetProj?.name || quickForm.project_name || 'General Project';

        await api.post('/api/v1/tasks', {
          project_id: pId,
          project_name: pName,
          title: quickForm.title || quickForm.name,
          description: quickForm.description,
          priority: quickForm.priority,
          status: 'todo',
          progress_percent: 0,
          due_date: quickForm.due_date ? quickForm.due_date : null,
          milestone_id: quickForm.milestone_id || null,
        });
        setQuickSuccess(`Task "${quickForm.title || quickForm.name}" created under "${pName}"!`);
      } else if (quickType === 'project') {
        await api.post('/api/v1/projects', {
          name: quickForm.name || quickForm.title,
          description: quickForm.description,
          priority: quickForm.priority,
          status: 'active',
        });
        setQuickSuccess(`Project "${quickForm.name || quickForm.title}" created!`);
      } else if (quickType === 'client') {
        await api.post('/api/v1/clients', {
          name: quickForm.name,
          email: quickForm.email,
          company: quickForm.company,
        });
        setQuickSuccess(`Client "${quickForm.name}" created!`);
      } else if (quickType === 'lead') {
        await api.post('/api/v1/leads', {
          contact_name: quickForm.name,
          email: quickForm.email,
          company: quickForm.company,
          estimated_value: Number(quickForm.amount) || 0,
        });
        setQuickSuccess(`Lead "${quickForm.name}" created!`);
      }

      setQuickForm({
        title: '',
        name: '',
        description: '',
        priority: 'medium',
        email: '',
        company: '',
        amount: '',
        project_id: quickProjects[0]?.id || '',
        project_name: quickProjects[0]?.name || '',
        milestone_id: '',
        due_date: '',
      });

      setTimeout(() => {
        setShowQuickAdd(false);
        setQuickSuccess(null);
      }, 1200);
    } catch (err: any) {
      alert(err.message || 'Failed to save item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateTo = (path: string) => {
    setShowSearch(false);
    setSearchQuery('');
    navigate(path);
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-brand-border bg-surface px-4 sm:px-6 z-10 select-none shadow-sm">
      {/* Left: Mobile hamburger & Workspace Identifier */}
      <div className="flex items-center space-x-2 sm:space-x-3">
        {onToggleMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleMobile}
            className="md:hidden h-8 w-8 text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40"
            title="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        <span className="text-xs uppercase tracking-wider text-brand-muted font-semibold hidden sm:inline">Workspace</span>
        <span className="text-brand-border font-bold hidden sm:inline">/</span>
        <div className="flex items-center space-x-2">
          {(() => {
            const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
            const logoSrc = isDark
              ? (branding.dark_logo_url || branding.logo_url)
              : (branding.logo_url || branding.dark_logo_url);
            return logoSrc && !topBarLogoError ? (
              <img
                src={logoSrc}
                alt={branding.workspace_name || 'Logo'}
                className="h-5 w-5 object-contain rounded"
                onError={() => setTopBarLogoError(true)}
              />
            ) : null;
          })()}
          <span className="text-sm font-bold text-brand-ink truncate">
            {branding.workspace_name ? `${branding.workspace_name} Agency OS` : 'Techosolution Agency OS'}
          </span>
        </div>
      </div>

      {/* Right Controls */}
      <div className="flex items-center space-x-4">
        {/* Global Search Shortcut */}
        <div
          onClick={() => setShowSearch(true)}
          className="relative hidden md:flex items-center cursor-pointer group"
          title="Search workspace (Ctrl+K)"
        >
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-brand-muted group-hover:text-brand transition-colors" />
          <input
            type="text"
            readOnly
            placeholder="Search tasks, projects (Ctrl+K)..."
            className="cursor-pointer h-9 w-64 rounded-input border border-brand-border bg-brand-surface pl-9 pr-14 text-xs text-brand-ink placeholder:text-brand-muted focus:bg-surface focus:outline-none focus:ring-2 focus:ring-brand/30 group-hover:border-brand-teal transition-colors"
          />
          <kbd className="absolute right-2 top-2 rounded bg-brand-tint/60 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-brand-ink pointer-events-none">
            Ctrl K
          </kbd>
        </div>

        {/* Live Timer Widget */}
        <div
          className={`flex items-center space-x-2 rounded-input border px-3 py-1.5 text-xs transition-colors ${
            timer
              ? 'border-brand bg-brand-tint/40 text-brand-ink font-semibold'
              : 'border-brand-border bg-brand-surface text-brand-muted'
          }`}
        >
          <div
            className={`h-2 w-2 rounded-full ${
              timer ? 'bg-brand animate-pulse' : 'bg-brand-border'
            }`}
          />
          {timer && (
            <span className="max-w-[120px] truncate font-medium text-brand-ink hidden xl:inline">
              {timer.task_title}
            </span>
          )}
          <span className="font-mono font-bold text-brand-ink">{formatTimer(seconds)}</span>
          {timer?.is_exceeded_threshold && (
            <span title="Please review this timer (> 8 hrs)">
              <AlertCircle className="h-3.5 w-3.5 text-status-warning inline" />
            </span>
          )}
          {timer ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleStopTimer}
              className="h-6 w-6 text-status-error hover:text-red-700 hover:bg-red-50"
              title="Stop timer"
            >
              <Square className="h-3 w-3 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-brand-muted hover:text-brand"
              title="No active timer"
              disabled
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Active Critical/High Blockers Alert Badge */}
        {criticalBlockersCount > 0 && (
          <button
            onClick={() => navigate('/blockers')}
            className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-input bg-red-500/10 dark:bg-red-950/40 border border-red-500/30 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-500/20 transition-all animate-pulse shadow-sm cursor-pointer"
            title={`${criticalBlockersCount} Active High/Critical Blocker(s) — Click to open Blockers Dashboard`}
          >
            <AlertOctagon className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
            <span className="hidden sm:inline font-semibold">
              {criticalBlockersCount} {criticalBlockersCount === 1 ? 'Blocker Alert' : 'Blocker Alerts'}
            </span>
            <span className="sm:hidden font-semibold">{criticalBlockersCount}</span>
          </button>
        )}

        {/* Milestone Completed Deliverable Alert Badge */}
        {completedMilestonesCount > 0 && (
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-input bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-500/20 transition-all animate-pulse shadow-sm cursor-pointer"
            title={`${completedMilestonesCount} Completed Milestone Deliverable(s) — Click to review deliverables`}
          >
            <Award className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span className="hidden sm:inline font-semibold">
              {completedMilestonesCount} {completedMilestonesCount === 1 ? 'Milestone Ready' : 'Milestones Ready'}
            </span>
            <span className="sm:hidden font-semibold">{completedMilestonesCount}</span>
          </button>
        )}

        {/* Quick Add Button: Hidden for Viewers */}
        {user?.role !== 'viewer' && (
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              setQuickType('task');
              setShowQuickAdd(true);
              setQuickSuccess(null);
            }}
            className="flex items-center space-x-1.5 font-semibold"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Quick Add</span>
          </Button>
        )}

        {/* In-App Notification Center */}
        <NotificationCenter />

        {/* Theme Toggle (Dark Mode) */}
        <ThemeToggle />

        {/* Keyboard Shortcuts Reference */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => window.dispatchEvent(new CustomEvent('tsos_open_shortcuts'))}
          className="h-8 w-8 text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40 hidden sm:flex"
          title="Keyboard Shortcuts (?)"
        >
          <Keyboard className="h-4 w-4" />
        </Button>

        {/* User Identity Avatar */}
        <div className="flex items-center space-x-2 pl-2 border-l border-brand-border">
          <div className="h-7 w-7 rounded-full bg-brand-tint flex items-center justify-center text-brand-ink font-bold text-xs border border-brand-border">
            {user?.name ? user.name.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
          </div>
          <div className="hidden lg:flex flex-col text-left">
            <span className="text-xs font-bold text-brand-ink">{user?.name || 'User'}</span>
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px] w-fit capitalize">
              {user?.role?.replace('_', ' ') || 'Guest'}
            </Badge>
          </div>
          <button
            onClick={() => logout()}
            className="p-1.5 text-brand-muted hover:text-status-error transition-colors ml-1"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Quick Add Modal */}
      {showQuickAdd && user?.role !== 'viewer' && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 relative">
            <button
              onClick={() => setShowQuickAdd(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="space-y-1">
              <h2 className="text-lg font-bold text-brand-navy">Quick Add</h2>
              <p className="text-xs text-slate-500">
                {user?.role === 'owner' || user?.role === 'manager'
                  ? 'Create tasks, projects, or agency items in 1 click.'
                  : 'Quickly create tasks assigned to your workflow.'}
              </p>
            </div>

            {quickSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-md flex items-center space-x-2">
                <Check className="h-4 w-4 text-emerald-600" />
                <span>{quickSuccess}</span>
              </div>
            )}

            {/* Entity Tabs - Developers and Team Members only get Tasks */}
            {(() => {
              const allowedTypes =
                user?.role === 'owner' || user?.role === 'manager'
                  ? (['task', 'project', 'client', 'lead'] as const)
                  : (['task'] as const);
              return (
                <div className="flex space-x-1 bg-slate-100 p-1 rounded-md text-xs font-medium">
                  {allowedTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setQuickType(type)}
                      className={`flex-1 py-1.5 rounded capitalize transition ${
                        quickType === type ? 'bg-white text-brand-navy shadow-sm font-semibold' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              );
            })()}

            <form onSubmit={handleQuickAddSubmit} className="space-y-3 pt-1">
              {quickType === 'task' && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Project *</label>
                    <select
                      required
                      value={quickForm.project_id}
                      onChange={(e) => {
                        const pid = e.target.value;
                        const pName = quickProjects.find((p) => p.id === pid)?.name || '';
                        setQuickForm({ ...quickForm, project_id: pid, project_name: pName, milestone_id: '' });
                      }}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-800"
                    >
                      {quickProjects.length === 0 ? (
                        <option value="">No projects available (Loading...)</option>
                      ) : (
                        quickProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Milestone Deliverable</label>
                    <select
                      value={quickForm.milestone_id}
                      onChange={(e) => setQuickForm({ ...quickForm, milestone_id: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-800"
                    >
                      <option value="">None (Independent Task)</option>
                      {quickMilestones
                        .filter((m) => {
                          const targetPid = quickForm.project_id || quickProjects[0]?.id;
                          const targetP = quickProjects.find((p) => p.id === targetPid || p.slug === targetPid);
                          return (
                            m.project_id === targetPid ||
                            (targetP?.slug && m.project_id === targetP.slug) ||
                            (targetP?.id && m.project_id === targetP.id)
                          );
                        })
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.title}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Task Title *</label>
                    <Input
                      required
                      placeholder="e.g. Implement header navigation drawer"
                      value={quickForm.title}
                      onChange={(e) => setQuickForm({ ...quickForm, title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Description / Scope Notes</label>
                    <textarea
                      rows={2}
                      placeholder="Optional task details or acceptance notes..."
                      value={quickForm.description}
                      onChange={(e) => setQuickForm({ ...quickForm, description: e.target.value })}
                      className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Priority</label>
                    <select
                      value={quickForm.priority}
                      onChange={(e) => setQuickForm({ ...quickForm, priority: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-700">Due Date (Optional)</label>
                      {quickForm.due_date && (
                        <button
                          type="button"
                          onClick={() => setQuickForm({ ...quickForm, due_date: '' })}
                          className="text-[10px] text-brand-teal hover:underline font-medium"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <Input
                      type="date"
                      value={quickForm.due_date || ''}
                      onChange={(e) => setQuickForm({ ...quickForm, due_date: e.target.value })}
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </>
              )}

              {quickType === 'project' && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Project Name *</label>
                    <Input
                      required
                      placeholder="e.g. WooCommerce Custom Theme Build"
                      value={quickForm.name}
                      onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Description / Notes</label>
                    <textarea
                      rows={3}
                      placeholder="Scope notes, staging URLs, client contact details..."
                      value={quickForm.description}
                      onChange={(e) => setQuickForm({ ...quickForm, description: e.target.value })}
                      className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Priority</label>
                    <select
                      value={quickForm.priority}
                      onChange={(e) => setQuickForm({ ...quickForm, priority: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </>
              )}

              {quickType === 'client' && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Client Name *</label>
                    <Input
                      required
                      placeholder="e.g. John Doe"
                      value={quickForm.name}
                      onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Company</label>
                    <Input
                      placeholder="e.g. Acme Corp"
                      value={quickForm.company}
                      onChange={(e) => setQuickForm({ ...quickForm, company: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Email Address</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={quickForm.email}
                      onChange={(e) => setQuickForm({ ...quickForm, email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              {quickType === 'lead' && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Contact Name *</label>
                    <Input
                      required
                      placeholder="e.g. Sarah Jenkins"
                      value={quickForm.name}
                      onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Company / Project</label>
                    <Input
                      placeholder="e.g. Jenkins Apparel"
                      value={quickForm.company}
                      onChange={(e) => setQuickForm({ ...quickForm, company: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Estimated Value ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g. 5000"
                      value={quickForm.amount}
                      onChange={(e) => setQuickForm({ ...quickForm, amount: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setShowQuickAdd(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="bg-brand-teal hover:bg-teal-700 text-white">
                  {isSubmitting ? 'Saving...' : `Create ${quickType.charAt(0).toUpperCase() + quickType.slice(1)}`}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ctrl+K Global Search Command Palette Modal */}
      {showSearch && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-start justify-center pt-20 p-4"
          onClick={() => {
            setShowSearch(false);
            setSearchQuery('');
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[75vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input Header */}
            <div className="p-3.5 border-b border-slate-200 flex items-center space-x-3 bg-slate-50/50">
              <Search className="h-5 w-5 text-brand-teal shrink-0" />
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const q = searchQuery.toLowerCase().trim();
                    if (!q) return;
                    const matchedProjects = searchData.projects.filter(
                      (p) =>
                        p.name?.toLowerCase().includes(q) ||
                        p.client_name?.toLowerCase().includes(q) ||
                        p.category?.toLowerCase().includes(q)
                    );
                    const matchedTasks = searchData.tasks.filter(
                      (t) =>
                        t.title?.toLowerCase().includes(q) ||
                        t.project_name?.toLowerCase().includes(q) ||
                        t.description?.toLowerCase().includes(q)
                    );
                    const matchedClients = searchData.clients.filter(
                      (c) =>
                        c.name?.toLowerCase().includes(q) ||
                        c.company?.toLowerCase().includes(q) ||
                        c.email?.toLowerCase().includes(q)
                    );
                    const matchedTeam = searchData.team.filter(
                      (u) =>
                        u.name?.toLowerCase().includes(q) ||
                        u.email?.toLowerCase().includes(q) ||
                        u.role?.toLowerCase().includes(q)
                    );

                    if (matchedProjects.length > 0) {
                      navigateTo(`/projects/${matchedProjects[0].id}`);
                    } else if (matchedTasks.length > 0) {
                      navigateTo(`/tasks/${matchedTasks[0].id}`);
                    } else if (matchedClients.length > 0) {
                      navigateTo('/clients');
                    } else if (matchedTeam.length > 0) {
                      navigateTo('/team');
                    }
                  }
                }}
                placeholder="Search projects, tasks, clients, team members (press Enter to open)..."
                className="w-full text-sm bg-transparent outline-none placeholder:text-slate-400 text-brand-navy"
              />
              <kbd className="hidden sm:inline-block rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-mono font-medium text-slate-500">
                ESC
              </kbd>
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery('');
                }}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-md"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Results Body */}
            <div className="overflow-y-auto divide-y divide-slate-100 p-2 flex-1">
              {searchQuery.trim() === '' ? (
                <div className="p-4 space-y-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Quick Navigation
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'All Projects', path: '/projects', icon: Briefcase, allowed: true },
                      { label: 'Task Board', path: '/tasks', icon: CheckSquare, allowed: true },
                      { label: 'My Tasks', path: '/my-tasks', icon: Check, allowed: true },
                      { label: 'Clients Directory', path: '/clients', icon: Users, allowed: user?.role === 'owner' || user?.role === 'manager' },
                      { label: 'Finance & Ledger', path: '/finance', icon: Square, allowed: user?.role === 'owner' },
                      { label: 'Settings & API Keys', path: '/settings', icon: User, allowed: user?.role === 'owner' },
                    ]
                      .filter((nav) => nav.allowed)
                      .map((nav) => {
                        const Icon = nav.icon;
                      return (
                        <button
                          key={nav.path}
                          onClick={() => {
                            navigateTo(nav.path);
                          }}
                          className="flex items-center space-x-2.5 p-2 rounded-lg text-left text-xs font-medium text-slate-700 hover:bg-slate-100/80 transition-colors"
                        >
                          <Icon className="h-4 w-4 text-brand-teal" />
                          <span>{nav.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (() => {
                const q = searchQuery.toLowerCase().trim();
                const matchedProjects = searchData.projects.filter(
                  (p) =>
                    p.name?.toLowerCase().includes(q) ||
                    p.client_name?.toLowerCase().includes(q) ||
                    p.category?.toLowerCase().includes(q)
                );
                const matchedTasks = searchData.tasks.filter(
                  (t) =>
                    t.title?.toLowerCase().includes(q) ||
                    t.project_name?.toLowerCase().includes(q) ||
                    t.description?.toLowerCase().includes(q)
                );
                const matchedClients = searchData.clients.filter(
                  (c) =>
                    c.name?.toLowerCase().includes(q) ||
                    c.company?.toLowerCase().includes(q) ||
                    c.email?.toLowerCase().includes(q)
                );
                const matchedTeam = searchData.team.filter(
                  (u) =>
                    u.name?.toLowerCase().includes(q) ||
                    u.email?.toLowerCase().includes(q) ||
                    u.role?.toLowerCase().includes(q)
                );

                const total =
                  matchedProjects.length +
                  matchedTasks.length +
                  matchedClients.length +
                  matchedTeam.length;

                if (total === 0) {
                  return (
                    <div className="p-8 text-center text-xs text-slate-500">
                      No results found for <span className="font-semibold text-slate-800">"{searchQuery}"</span>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4 p-2">
                    {/* Projects Section */}
                    {matchedProjects.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2">
                          Projects ({matchedProjects.length})
                        </div>
                        {matchedProjects.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => {
                              navigateTo(`/projects/${p.id}`);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100/80 text-left transition-colors"
                          >
                            <div className="flex items-center space-x-2.5">
                              <Briefcase className="h-4 w-4 text-brand-teal shrink-0" />
                              <div>
                                <div className="text-xs font-semibold text-brand-navy">{p.name}</div>
                                <div className="text-[10px] text-slate-400">
                                  {p.client_name || 'Internal'} • {p.category || 'General'}
                                </div>
                              </div>
                            </div>
                            <Badge variant={p.status as any} className="text-[9px] capitalize">
                              {p.status}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Tasks Section */}
                    {matchedTasks.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2">
                          Tasks ({matchedTasks.length})
                        </div>
                        {matchedTasks.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => {
                              navigateTo(`/tasks/${t.id}`);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100/80 text-left transition-colors"
                          >
                            <div className="flex items-center space-x-2.5">
                              <CheckSquare className="h-4 w-4 text-blue-600 shrink-0" />
                              <div>
                                <div className="text-xs font-semibold text-brand-navy">{t.title}</div>
                                <div className="text-[10px] text-slate-400">{t.project_name}</div>
                              </div>
                            </div>
                            <Badge variant={t.priority as any} className="text-[9px] uppercase">
                              {t.priority}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Clients Section */}
                    {matchedClients.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2">
                          Clients ({matchedClients.length})
                        </div>
                        {matchedClients.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => {
                              navigateTo('/clients');
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100/80 text-left transition-colors"
                          >
                            <div className="flex items-center space-x-2.5">
                              <Users className="h-4 w-4 text-amber-600 shrink-0" />
                              <div>
                                <div className="text-xs font-semibold text-brand-navy">{c.name}</div>
                                <div className="text-[10px] text-slate-400">{c.company || c.email}</div>
                              </div>
                            </div>
                            <span className="text-[10px] text-slate-400">Client</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Team Section */}
                    {matchedTeam.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2">
                          Team ({matchedTeam.length})
                        </div>
                        {matchedTeam.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => {
                              navigateTo('/team');
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100/80 text-left transition-colors"
                          >
                            <div className="flex items-center space-x-2.5">
                              <User className="h-4 w-4 text-slate-600 shrink-0" />
                              <div>
                                <div className="text-xs font-semibold text-brand-navy">{u.name}</div>
                                <div className="text-[10px] text-slate-400">{u.email}</div>
                              </div>
                            </div>
                            <Badge variant="secondary" className="text-[9px] capitalize">
                              {u.role}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
